import React from 'react';

interface Company {
  id: string;
  name: string;
  type: string;
  aum: number;
  salary: number;
  bonus: number;
  carry: number;
  checked: boolean;
}

interface AIAgentProps {
  companies: Company[];
}

const AIAgent: React.FC<AIAgentProps> = ({ companies }) => {
  // Анализируем данные для генерации выводов
  const companiesWithCarry = companies.filter(company => company.carry > 0).length;
  const avgSalary = companies.length > 0 ? companies.reduce((sum, c) => sum + c.salary, 0) / companies.length : 0;
  const avgBonus = companies.length > 0 ? companies.reduce((sum, c) => sum + c.bonus, 0) / companies.length : 0;
  const peCompanies = companies.filter(c => c.type === 'PE').length;
  const foCompanies = companies.filter(c => c.type === 'Family Office').length;
  
  // Анализ по AUM
  const largeAumCompanies = companies.filter(c => c.aum >= 5).length;
  const mediumAumCompanies = companies.filter(c => c.aum >= 3 && c.aum < 5).length;
  const smallAumCompanies = companies.filter(c => c.aum >= 1 && c.aum < 3).length;
  const tinyAumCompanies = companies.filter(c => c.aum < 1).length;

  const generateInsights = () => {
    const insights = [];
    
    // Анализ по типам компаний
    if (peCompanies > foCompanies) {
      insights.push("В выборке преобладают Private Equity компании, что отражает их активность на рынке.");
    } else if (foCompanies > peCompanies) {
      insights.push("В выборке больше Family Office, что показывает их растущее присутствие в индустрии.");
    } else if (peCompanies === foCompanies && peCompanies > 0) {
      insights.push("В выборке равное количество PE и FO компаний, что обеспечивает сбалансированный анализ.");
    }
    
    // Анализ carry
    if (companiesWithCarry > 0) {
      const carryPercentage = ((companiesWithCarry / companies.length) * 100).toFixed(1);
      insights.push(`Только ${carryPercentage}% компаний предлагают carry interest, что делает его премиальным компонентом компенсации.`);
    } else {
      insights.push("Ни одна из компаний в выборке не предлагает carry interest.");
    }
    
    // Анализ зарплат
    if (avgSalary > 250000) {
      insights.push(`Средняя зарплата в выборке составляет ${Math.round(avgSalary).toLocaleString('ru-RU')} ₽, что указывает на высокий уровень компенсации в индустрии.`);
    } else if (avgSalary > 0) {
      insights.push(`Средняя зарплата в выборке составляет ${Math.round(avgSalary).toLocaleString('ru-RU')} ₽.`);
    }
    
    // Анализ бонусов
    if (avgBonus > 100) {
      insights.push(`Высокий уровень годового премирования (в среднем ${Math.round(avgBonus)}%) показывает значимость переменной части компенсации.`);
    } else if (avgBonus > 0) {
      insights.push(`Средний уровень годового премирования составляет ${Math.round(avgBonus)}%.`);
    }
    
    // Анализ по AUM
    if (largeAumCompanies > 0) {
      insights.push(`В выборке ${largeAumCompanies} компаний с AUM >= 5млрд$, что представляет крупные игроки рынка.`);
    }
    if (mediumAumCompanies > 0) {
      insights.push(`Средние компании (AUM 3-5млрд$) представлены ${mediumAumCompanies} участниками.`);
    }
    if (smallAumCompanies > 0) {
      insights.push(`Малые компании (AUM 1-3млрд$) составляют ${smallAumCompanies} участников выборки.`);
    }
    if (tinyAumCompanies > 0) {
      insights.push(`Микро-компании (AUM < 1млрд$) представлены ${tinyAumCompanies} участниками.`);
    }
    
    return insights;
  };

  const insights = generateInsights();

  return (
    <div style={{
      width: '100%',
      maxWidth: 800,
      margin: '32px auto',
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e1e5e9'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '20px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '18px',
          fontWeight: 'bold'
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
          </svg>
        </div>
        <h3 style={{
          margin: 0,
          fontSize: '20px',
          fontWeight: 'bold',
          color: '#1a237e',
          fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif"
        }}>
          Помощник datacircle-ai
        </h3>
      </div>
      
      <div style={{
        background: '#f8f9fa',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <h4 style={{
          margin: '0 0 16px 0',
          fontSize: '16px',
          fontWeight: '600',
          color: '#1a237e',
          fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif"
        }}>
          Ключевые выводы по выборке ({companies.length} компаний):
        </h4>
        <ul style={{
          margin: 0,
          paddingLeft: '20px',
          color: '#333',
          lineHeight: '1.6',
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
          filter: 'blur(4px)',
          opacity: 0.5
        }}>
          {insights.map((insight, index) => (
            <li key={index} style={{ marginBottom: '8px' }}>
              {insight}
            </li>
          ))}
        </ul>
      </div>
      
      <div style={{
        fontSize: '14px',
        color: '#666',
        fontStyle: 'italic',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif"
      }}>
        Анализ основан на {companies.length} компаниях в выборке. 
        Данные обновлены в реальном времени в соответствии с выбранными фильтрами на шаге 2.
      </div>
    </div>
  );
};

export default AIAgent; 